import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { getFilteredPositionIds } from "../../../db/DexieServises";
import { setOutletTypes } from "../../../redux-toolkit/reducers/OutletTypeSlice";

interface EffortReportItem {
  PrimaryDcrAverage: string;
  SecondaryCallAvg: string;
}

interface DailyCallAverageProps {
  selectedPositionIds?: (string | number)[];
}

const DailyCallAverage: React.FC<DailyCallAverageProps> = ({ selectedPositionIds }) => {
  const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([]);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [monthsList, setMonthsList] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [monthRange, setMonthRange] = useState({
    from: { month: "", year: "" },
    to: { month: "", year: "" },
  });

  const dispatch = useDispatch();
  const positions = useSelector((state: RootState) => state.position.positions);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const monthNumberToName: { [key: number]: string } = {
    1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
    7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
  };

  const nameToMonthNumber: { [key: string]: number } = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };

  const toggleMonthModal = () => setMonthModalOpen((prev: boolean) => !prev);

  const handleApplyDateRange = () => {
    if (monthRange.from.month && monthRange.from.year && monthRange.to.month && monthRange.to.year) {
      const fromMonthNum = nameToMonthNumber[monthRange.from.month];
      const toMonthNum = nameToMonthNumber[monthRange.to.month];
      setDateRange({
        start: `${fromMonthNum}-${monthRange.from.year}`,
        end: `${toMonthNum}-${monthRange.to.year}`,
      });
    }
    toggleMonthModal();
  };

  const getMonthRangeButtonLabel = () => {
    if (dateRange.start && dateRange.end) {
      const [startMonth, startYear] = dateRange.start.split("-");
      const [endMonth, endYear] = dateRange.end.split("-");
      return `${monthNumberToName[parseInt(startMonth, 10)]} ${startYear} - ${monthNumberToName[parseInt(endMonth, 10)]} ${endYear}`;
    }
    return "Select Month Range";
  };

  const primaryOutletType = useSelector((state: RootState) => state.outletType.primaryOutletType);
  const secondaryOutletType = useSelector((state: RootState) => state.outletType.secondaryOutletType);
  const primaryOutletName = useSelector((state: RootState) => state.outletTypeName.OutlettypeNamePrimary);
  const secondaryOutletName = useSelector((state: RootState) => state.outletTypeName.OutlettypeNameSecondary);

  /**
   * Converts "MMM-yyyy" to "yyyy-MM" format
   */
  const formatMonth = (month: string): string => {
    const [monthName, year] = month.split("-");
    const monthNumber = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
    return `${year}-${monthNumber.toString().padStart(2, "0")}`;
  };

  /**
   * Fetch Effort Report data for given positions and month
   */
  const fetchEffortReport = useCallback(
    async (positionIds: string, month: string): Promise<EffortReportItem[]> => {
      const serverUrl = localStorage.getItem("serverUrl");
      if (!serverUrl) {
        console.error("Server URL not found in localStorage.");
        return [];
      }

      const url = `${serverUrl}${API.getEffortReport}`;
      console.log(month, positionIds, '=======================month param---', dateRange);

      try {
        const { status, data } = await getData(url, true, {
          position_id: positionIds,
          moye: month,
        });

        if (status === 200 && Array.isArray(data.data)) {
          return data.data;
        } else {
          console.error("Failed to fetch effort report data");
          return [];
        }
      } catch (error) {
        console.error("Error fetching effort report:", error);
        return [];
      }
    },
    [dateRange]
  );

  /**
   * Process API data and prepare chart values
   */
  const processChartData = useCallback(async () => {
    // Use selectedPositionIds from props if provided, otherwise use Redux positions
    const positionIds =
      selectedPositionIds && selectedPositionIds.length > 0
        ? selectedPositionIds.join(",")
        : positions.length > 0
          ? positions.map((pos) => pos.id).join(",")
          : (await getFilteredPositionIds("Position", unitId)).join(",");

    const monthsToUse = monthsList.length > 0 ? monthsList : [];

    const chartData = { primary: [] as number[], secondary: [] as number[] };

    for (const month of monthsToUse) {
      const apiData = await fetchEffortReport(positionIds, month);

      const avgPrimary =
        apiData.length > 0
          ? apiData.reduce((sum, item) => sum + parseFloat(item.PrimaryDcrAverage || "0"), 0) /
            apiData.length
          : 0;

      const avgSecondary =
        apiData.length > 0
          ? apiData.reduce((sum, item) => sum + parseFloat(item.SecondaryCallAvg || "0"), 0) /
            apiData.length
          : 0;

      chartData.primary.push(avgPrimary);
      chartData.secondary.push(avgSecondary);
    }

    setChartSeries([
      { name: primaryOutletName || "Primary Outlet", data: chartData.primary },
      { name: secondaryOutletName || "Secondary Outlet", data: chartData.secondary },
    ]);

    setChartOptions({
      chart: {
        type: "line",
        height: 400,
        toolbar: {
          tools: {
            download: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      // title: { text: "Calls Average (Monthly)" },
      xaxis: {
        categories: monthsToUse,
        title: { text: "Month" },
      },
      yaxis: {
        title: { text: "Number of Calls" },
        min: 0,
        max: 10,
        labels: { formatter: (val: number) => Math.round(val).toString() },
      },
      tooltip: {
        y: { formatter: (val: number) => Math.round(val).toString() },
      },
      colors: ["#4285F4", "#DB4437"],
      legend: { position: "top" },
    });
  }, [positions, unitId, monthsList, primaryOutletName, secondaryOutletName, fetchEffortReport, selectedPositionIds]);

  /**
   * Builds month list from selected date range
   */
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const startMonth = parseInt(dateRange.start.split("-")[0], 10);
      const startYear = parseInt(dateRange.start.split("-")[1], 10);
      const endMonth = parseInt(dateRange.end.split("-")[0], 10);
      const endYear = parseInt(dateRange.end.split("-")[1], 10);

      const months: string[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const monthStart = year === startYear ? startMonth : 1;
        const monthEnd = year === endYear ? endMonth : 12;

        for (let month = monthStart; month <= monthEnd; month++) {
          const formattedMonth = `${year}-${month.toString().padStart(2, "0")}`;
          months.push(formattedMonth);
        }
      }

      setMonthsList(months);
    } else {
      // Default to last 3 months if dateRange is not selected
      const months: string[] = [];
      const now = new Date();
      for (let i = 0; i < 3; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(`${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`);
      }
      setMonthsList(months.reverse()); // Reverse to have oldest first
    }
  }, [dateRange]);

  /**
   * Recalculate chart whenever dependencies change
   */
  useEffect(() => {
    if (monthsList.length > 0) {
      processChartData();
    }
  }, [positions, dateRange, processChartData, selectedPositionIds, monthsList]);

  /**
   * Re-render chart when outlet names change
   */
  useEffect(() => {
    processChartData();
  }, [primaryOutletName, secondaryOutletName, processChartData]);

  return (
    <Col className="col-sm-12 col-xl-6 box-col-6">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold fs-5"> Call Average</h5>
            <Button color="info" onClick={toggleMonthModal} size="sm">
              {getMonthRangeButtonLabel()}
            </Button>
          </div>

          {/* Date Range Modal */}
          <Modal isOpen={monthModalOpen} toggle={toggleMonthModal} centered>
            <ModalHeader toggle={toggleMonthModal}>Select Month Range</ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <label>From</label>
                  <select
                    className="form-control"
                    value={`${monthRange.from.month}-${monthRange.from.year}`}
                    onChange={(e) => {
                      const [month, year] = e.target.value.split("-");
                      setMonthRange({ ...monthRange, from: { month, year } });
                    }}
                  >
                    <option value="">Select Month</option>
                    {yearOptions.flatMap((year) =>
                      Object.entries(monthNumberToName)
                        .sort(([a], [b]) => Number(b) - Number(a))
                        .filter(([monthNumber]) => {
                          if (year === currentYear) {
                            return Number(monthNumber) <= new Date().getMonth() + 1;
                          }
                          return true;
                        })
                        .map(([_, monthName]) => (
                          <option
                            key={`from-${monthName}-${year}`}
                            value={`${monthName}-${year}`}
                          >
                            {`${monthName}-${year}`}
                          </option>
                        ))
                    )}
                  </select>
                </Col>
                <Col md={6}>
                  <label>To</label>
                  <select
                    className="form-control"
                    value={`${monthRange.to.month}-${monthRange.to.year}`}
                    onChange={(e) => {
                      const [month, year] = e.target.value.split("-");
                      setMonthRange({ ...monthRange, to: { month, year } });
                    }}
                  >
                    <option value="">Select Month</option>
                    {yearOptions.flatMap((year) =>
                      Object.entries(monthNumberToName)
                        .sort(([a], [b]) => Number(b) - Number(a))
                        .filter(([monthNumber]) => {
                          if (year === currentYear) {
                            return Number(monthNumber) <= new Date().getMonth() + 1;
                          }
                          return true;
                        })
                        .map(([_, monthName]) => (
                          <option
                            key={`to-${monthName}-${year}`}
                            value={`${monthName}-${year}`}
                          >
                            {`${monthName}-${year}`}
                          </option>
                        ))
                    )}
                  </select>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleMonthModal}>
                Cancel
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  setMonthRange({ from: { month: "", year: "" }, to: { month: "", year: "" } });
                  setDateRange({ start: "", end: "" });
                  toggleMonthModal();
                }}
              >
                Clear
              </Button>
              <Button color="success" onClick={handleApplyDateRange}>
                Apply
              </Button>
            </ModalFooter>
          </Modal>

          <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={400} />
        </CardBody>
      </Card>
    </Col>
  );
};

export default DailyCallAverage;
